import type { CSSProperties, ReactNode } from "react";
import { useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAtomValue } from "jotai";
import { textColorAtom } from "../state/textColor";
import { entrancePhaseAtom, isChromePhase } from "../state/entrance";
import { ROUTES } from "../routes";
import { animationConfig } from "../config/animation";

/** Each color-mode layer is itself positioned, at the top of the visible
 *  area: absolute, not fixed, because iOS Safari samples fixed layers to
 *  tint its bars (utils/edgeToEdge); the window never scrolls, so it stays
 *  put all the same. The outer <nav> must NOT be positioned — a
 *  positioned layer with a z-index creates a stacking context, and
 *  `mix-blend-mode` only blends within its own stacking context. If the
 *  parent created one, the blend layer would only see its sibling solid
 *  layer (a transparent box) as its backdrop instead of the page, and the
 *  blend silently does nothing. With the parent at default flow, each
 *  layer sits in the root stacking context and blends against the
 *  actual page content beneath. */
const LAYER_CLASSES =
  "absolute top-[var(--bleed)] left-0 w-full z-[15] pointer-events-none " +
  "select-none font-sans text-ui font-[400] sm:font-[300] tracking-wide " +
  "px-6 py-5 md:p-[3em] flex justify-between items-center";

/**
 * Site-wide top navigation.
 *
 * Two color-mode layers cross-fade via opacity:
 *  - **Blend layer** — `text-white mix-blend-difference`, used on
 *    portfolio/index/about where the bg varies per slide.
 *  - **Solid layer** — `color: textColor` driven by the active case study.
 *
 * Reason: `mix-blend-mode` can't be CSS-transitioned. Toggling it directly
 * while `color` smoothly tweens produced a visible mid-transition flash
 * (intermediate colors rendered through a half-applied blend on a
 * half-covered bg). Cross-fading two stable layers via opacity keeps each
 * layer's appearance correct end-to-end. Duration is synced to
 * `caseStudy.chrome` so the swap matches the panel slide-in/out.
 *
 * Whichever layer is currently invisible gets `inert` + `aria-hidden` +
 * per-Link `pointer-events: none` so Tab focus and click hit-testing
 * never land on the hidden copy.
 */
export default function Nav() {
  const textColor = useAtomValue(textColorAtom);
  const transition = `opacity ${animationConfig.caseStudy.chrome.durationMs}ms ${animationConfig.caseStudy.chrome.easing}`;
  const blendActive = !textColor;

  // Retain the most recent non-null textColor on the solid layer so its
  // color stays put during the fade-out. Without this, the inline `color`
  // is yanked the instant textColor becomes null, and the text snaps from
  // red → inherited black BEFORE the opacity transition can hide it —
  // which reads as the colored nav "just disappearing" on close.
  const lastSolidColorRef = useRef<string | undefined>(undefined);
  if (textColor) lastSolidColorRef.current = textColor;

  return (
    // Outer <nav> stays at default flow (no positioning) so it doesn't
    // create a stacking context that would trap the children's blending.
    // Both children are `position: fixed` siblings in the root context.
    <nav>
      <NavLayer
        active={blendActive}
        transition={transition}
        className={`${LAYER_CLASSES} text-white mix-blend-difference`}
      >
        <NavLinks interactive={blendActive} />
      </NavLayer>
      <NavLayer
        active={!blendActive}
        transition={transition}
        className={LAYER_CLASSES}
        style={{ color: lastSolidColorRef.current }}
      >
        <NavLinks interactive={!blendActive} />
      </NavLayer>
    </nav>
  );
}

/** A single color-mode layer. Only the active layer is interactive. */
function NavLayer({
  active,
  transition,
  className,
  style,
  children,
}: {
  active: boolean;
  transition: string;
  className: string;
  style?: CSSProperties;
  children: ReactNode;
}) {
  return (
    <div
      className={className}
      style={{ ...style, opacity: active ? 1 : 0, transition }}
      aria-hidden={!active}
      {...(active ? {} : { inert: "" })}
    >
      {children}
    </div>
  );
}

/** Nav link hover opacity transition. Symmetric ease because hover/unhover
 *  are equally weighted. Centralised in animationConfig.nav.linkHover. */
const LINK_HOVER_TRANSITION = `opacity ${animationConfig.nav.linkHover.durationMs}ms ${animationConfig.nav.linkHover.easing}`;

/** During the portfolio's first-load entrance the name is the brand word
 *  of the loader: it sits here, top left, from the first frame; the
 *  subtitle and the links come in as the slides settle. */
const CHROME_IN = animationConfig.portfolio.entrance.chromeIn;
const CHROME_IN_TRANSITION = `opacity ${CHROME_IN.durationMs}ms ${CHROME_IN.easing}`;

/** Nav link content. `interactive` toggles `pointer-events` on the two
 *  click-capturing wrappers so the inactive layer doesn't intercept clicks
 *  meant for the visible one. */
function NavLinks({ interactive }: { interactive: boolean }) {
  const location = useLocation();
  const onPortfolio = location.pathname === ROUTES.index;
  const onAbout = location.pathname === ROUTES.about;
  const pe = interactive ? "pointer-events-auto" : "pointer-events-none";
  // Only the name while the portfolio's loader is up (see CHROME_IN). The
  // links leave instantly: the phase flips to "boot" right after the first
  // paint, and easing that out would show them fading on a cold load.
  const chromeIn = isChromePhase(useAtomValue(entrancePhaseAtom));
  const chromeStyle: CSSProperties = { opacity: chromeIn ? 1 : 0, transition: chromeIn ? CHROME_IN_TRANSITION : "none" };

  return (
    <>
      <Link to={ROUTES.index} className={`${pe} ${onPortfolio ? "cursor-default" : ""}`}>
        <p>Oliver Hassan</p>
        <p className="sm:block hidden" style={chromeStyle}>
          Software Engineer & Creative Developer
        </p>
      </Link>
      <div className={`flex gap-[2em] ${pe}`} style={chromeStyle} {...(chromeIn ? {} : { inert: "" })}>
        <Link
          to={ROUTES.about}
          aria-current={onAbout ? "page" : undefined}
          onMouseEnter={() => {
            void import("../pages/Portfolio");
            void import("../components/Portfolio/AboutPanel");
            void import("../components/Portfolio/FlyingBook").then((m) => m.warmFlyingBook());
          }}
        >
          <p
            className={`hover:opacity-100 ${onAbout ? "opacity-100 cursor-default" : "opacity-50"}`}
            style={{ transition: LINK_HOVER_TRANSITION }}
          >
            About
          </p>
        </Link>
        <Link
          to={ROUTES.index}
          aria-current={onPortfolio ? "page" : undefined}
          onMouseEnter={() => {
            void import("../pages/Portfolio");
          }}
        >
          <p
            className={`hover:opacity-100 ${onPortfolio ? "opacity-100 cursor-default" : "opacity-50"}`}
            style={{ transition: LINK_HOVER_TRANSITION }}
          >
            Portfolio
          </p>
        </Link>
      </div>
    </>
  );
}
